import {
  ComponentProps,
  ComponentType,
  createContext,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { NavLink, useHistory, useRouteMatch } from "react-router-dom";
import { Anchor, useLeftBar } from "./LeftBar";
import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { SIDE_NAV_BUTTON_CLICKED } from "constants/analyticsEvents";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAnalytics } from "hooks/useAnalytics";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { useSecondaryNav } from "components/AppLayout/SecondaryNavContext";
import { usePageTitleSetter } from "hooks/usePageTitleSetter";

type SubMenuContextProps = {
  title?: string;
  defaultRoute?: string;
  isSubMenuActive: boolean;
  isTriggerActive: boolean;
  useParentPath?: boolean;
  isExpanded: boolean;
  isOtherExpanded: boolean;
  toggleExpanded: () => void;
};

const NOOP = () => undefined;

const SubMenuContext = createContext<SubMenuContextProps>({
  isSubMenuActive: false,
  isTriggerActive: false,
  isExpanded: false,
  isOtherExpanded: false,
  toggleExpanded: NOOP,
});

const useSubMenu = () => useContext(SubMenuContext);

const SubMenuRoot = ({
  children,
  defaultRoute,
  childPaths,
  excludePaths,
  title,
  useParentPath = true,
}: {
  children: ReactNode;
  defaultRoute?: string;
  childPaths?: string[];
  excludePaths?: string[];
  title?: ReactNode;
  useParentPath?: boolean;
}) => {
  const { location } = useHistory();
  const { registerActive, headerSlot, expandedMenu, setExpandedMenu } =
    useSecondaryNav();
  const menuId = typeof title === "string" ? title : defaultRoute ?? "";
  const isExpandedOnThisPage = expandedMenu?.pathname === location?.pathname;
  const isExpanded = isExpandedOnThisPage && expandedMenu?.menuId === menuId;
  const isOtherExpanded = isExpandedOnThisPage && !isExpanded;

  const toggleExpanded = () =>
    setExpandedMenu?.(
      isExpanded ? null : { menuId, pathname: location?.pathname }
    );

  const isTriggerActive = Boolean(
    childPaths?.some((path) => location?.pathname?.includes(path))
  );

  const isSubMenuActive =
    isTriggerActive &&
    !excludePaths?.some((path) => location?.pathname?.includes(path));

  useEffect(() => {
    if (isSubMenuActive) {
      return registerActive();
    }
  }, [isSubMenuActive, registerActive]);

  return (
    <SubMenuContext.Provider
      value={{
        title: typeof title === "string" ? title : undefined,
        defaultRoute,
        isSubMenuActive,
        isTriggerActive,
        useParentPath,
        isExpanded,
        isOtherExpanded,
        toggleExpanded,
      }}
    >
      {children}
      {isSubMenuActive &&
        headerSlot &&
        createPortal(
          <span className="t-truncate t-text-body t-font-medium t-text-neutral-40">
            {title}
          </span>,
          headerSlot
        )}
    </SubMenuContext.Provider>
  );
};

const SubMenuTrigger = ({
  children,
  icon: Icon,
}: {
  children: ReactNode;
  icon: ComponentType;
}) => {
  const { open } = useLeftBar();
  const {
    defaultRoute,
    isTriggerActive,
    useParentPath,
    isSubMenuActive,
    isOtherExpanded,
    toggleExpanded,
  } = useSubMenu();
  const { isInline } = useSecondaryNav();
  const { path } = useRouteMatch();

  // Inline (mobile nav) the active sub-item carries the highlight, so the
  // trigger only keeps its bold label.
  const isHighlighted = isTriggerActive && !isInline;
  const { currentApp } = useCurrentAppContext();
  const { trackEvent } = useAnalytics();

  const to =
    useParentPath === false
      ? defaultRoute ?? ""
      : `${path}${defaultRoute ?? ""}`;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    trackEvent(SIDE_NAV_BUTTON_CLICKED, {
      app_name: currentApp.name,
      name: children as string,
    });

    if (isInline && (!isSubMenuActive || isOtherExpanded)) {
      event.preventDefault();
      event.stopPropagation();
      toggleExpanded();
    }
  };

  return (
    <ConditionalToolTip condition={!open && children} side="right">
      <NavLink
        to={to}
        onClick={handleClick}
        className={classNames(
          "t-relative t-my-0.5 t-flex t-h-9 t-w-full t-items-center t-gap-2 t-overflow-hidden t-rounded-md t-no-underline t-transition-all",
          {
            "t-px-3": open,
            "t-justify-center t-px-0": !open,
            "hover:t-bg-neutral-0": !isHighlighted,
            "t-bg-neutral-0": isHighlighted,
          }
        )}
      >
        <span
          className={classNames(
            "t-absolute t-left-0 t-top-1/2 t-h-4 t-w-0.5 -t-translate-y-1/2 t-rounded-r t-bg-purple-70",
            { "t-hidden": !isHighlighted }
          )}
        />
        <span
          className={classNames(
            "t-flex t-h-5 t-w-5 t-shrink-0 t-items-center t-justify-center",
            {
              "t-text-text-100": isTriggerActive,
              "t-text-text-30": !isTriggerActive,
            }
          )}
        >
          <Icon />
        </span>
        {open && (
          <span
            className={classNames("t-text-body-sm t-whitespace-nowrap", {
              "t-text-text-100 t-font-medium": isTriggerActive,
              "!t-text-text-60": !isTriggerActive,
            })}
          >
            {children}
          </span>
        )}
      </NavLink>
    </ConditionalToolTip>
  );
};

const SubMenuContainer = ({
  children,
}: {
  children: ReactNode;
  hoverTitle?: string;
}) => {
  const { isSubMenuActive, isExpanded, isOtherExpanded } = useSubMenu();
  const { bodySlot, isInline } = useSecondaryNav();

  if (isInline && isExpanded && !isSubMenuActive) {
    return <div className="t-flex t-flex-col t-gap-0.5 t-pl-4">{children}</div>;
  }

  if (!isSubMenuActive || (isInline && isOtherExpanded)) {
    return null;
  }

  if (isInline) {
    return <div className="t-flex t-flex-col t-gap-0.5 t-pl-4">{children}</div>;
  }

  if (!bodySlot) {
    return null;
  }

  return createPortal(
    <div className="t-flex t-flex-col t-gap-0.5">{children}</div>,
    bodySlot
  );
};

const SubMenuItem = ({
  useParentPath,
  to,
  disabled,
  suffix,
  exact,
  activePaths,
  children,
}: {
  useParentPath: boolean;
  to: string;
  disabled?: boolean;
  suffix?: ReactNode;
  exact?: boolean;
  activePaths?: string[];
  children: ReactNode;
}) => {
  const { path } = useRouteMatch();
  const { location } = useHistory();
  const { title: subMenuTitle } = useSubMenu();
  const { currentApp } = useCurrentAppContext();
  const { trackEvent } = useAnalytics();
  const setPageTitle = usePageTitleSetter();

  const isExternal =
    to?.startsWith("https://") ||
    to?.startsWith("http://") ||
    to?.startsWith("mailto") ||
    to?.startsWith("tel");

  const LinkComponent = isExternal ? Anchor : NavLink;

  const toPath = (useParentPath ? `${path}${to}` : to)?.split("?")?.[0];
  const toQuery = to?.split("?")?.[1];
  const toTab = toQuery ? new URLSearchParams(toQuery).get("tab") : null;
  const currentTab = new URLSearchParams(location?.search).get("tab");

  let isSelected = Boolean(location?.pathname?.includes(to?.split("?")?.[0]));
  if (exact) isSelected = location?.pathname === toPath;
  if (toTab) isSelected = isSelected && currentTab === toTab;

  if (activePaths?.some((path) => location?.pathname?.includes(path))) {
    isSelected = true;
  }

  const handleLink = () => {
    trackEvent(SIDE_NAV_BUTTON_CLICKED, {
      app_name: currentApp.name,
      name: children as string,
      sub_menu: subMenuTitle,
    });
  };

  useEffect(() => {
    if (isSelected && typeof children === "string") {
      setPageTitle(children);
    }
  }, [isSelected, children, setPageTitle]);

  return (
    <LinkComponent
      to={useParentPath ? `${path}${to}` : to}
      target={to?.includes("http") ? "_blank" : undefined}
      onClick={handleLink}
      key={to}
      className={classNames(
        "t-relative t-my-0.5 t-flex t-items-center t-gap-2 t-rounded-md t-px-3 t-py-2 t-no-underline t-transition-all",
        {
          "t-pointer-events-none": disabled,
          "hover:t-bg-neutral-0": !isSelected,
          "t-bg-neutral-0": isSelected,
        }
      )}
    >
      <span
        className={classNames(
          "t-absolute t-left-0 t-top-1/2 t-h-4 t-w-0.5 -t-translate-y-1/2 t-rounded-r t-bg-purple-70",
          { "t-hidden": !isSelected }
        )}
      />
      <span
        className={classNames(
          "t-text-body-sm t-whitespace-nowrap t-capitalize",
          {
            "t-text-text-30": disabled,
            "t-text-text-100 t-font-medium": isSelected && !disabled,
            "t-text-text-60": !isSelected && !disabled,
          }
        )}
      >
        {children}
      </span>
      {suffix}
    </LinkComponent>
  );
};

const SubMenuBaseItem = (props: ComponentProps<typeof SubMenuItem>) => {
  const { path } = useRouteMatch();
  const currentPath =
    props.useParentPath === false ? props.to : `${path}${props.to}`;

  return (
    <PermissionBasedView
      key={props.to}
      side="right"
      isNav
      route={currentPath.split("?")[0]}
      blockedUI={
        <SubMenuItem
          suffix={<DiamondPurple height={12} width={12} />}
          {...props}
        />
      }
    >
      <SubMenuItem {...props} />
    </PermissionBasedView>
  );
};

export const SubMenu = {
  Trigger: SubMenuTrigger,
  Container: SubMenuContainer,
  Item: SubMenuBaseItem,
  Root: SubMenuRoot,
};
